import React, { useState, useEffect, useCallback } from 'react';
import { Upload, message } from 'antd';
import type { UploadProps } from 'antd';
import { PlusOutlined, LoadingOutlined, DeleteOutlined } from '@ant-design/icons';
import { uploadFile, getFileUrlFromResponse } from '../../api/upload';
import './index.css';

const DEFAULT_ACCEPT = 'image/jpeg,image/png';
const DEFAULT_MAX_SIZE_MB = 2;
const DEFAULT_MAX_FILES = 9;
/** 上传接口返回的 File 为相对路径时，预览/回显用此 CDN 前缀 */
const DEFAULT_CDN_PREFIX = 'https://cdn.upyun.mallimg.shjinjia.com.cn';

/** 扩展名列表转 MIME；已是 MIME 则原样返回 */
function acceptedFilesToAccept(acceptedFiles: string): string {
  const s = (acceptedFiles || '').trim();
  if (!s) return DEFAULT_ACCEPT;
  if (s.includes('image/') || s.includes('application/')) return s;
  const exts = s.split(/[,;\s]+/).map((e) => e.trim().toLowerCase().replace(/^\./, ''));
  const mimeMap: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
  };
  const mimes = [...new Set(exts.map((e) => mimeMap[e] || `image/${e}`).filter(Boolean))];
  return mimes.length ? mimes.join(',') : DEFAULT_ACCEPT;
}

function isFileAccepted(file: File, accept: string): boolean {
  const types = accept.split(',').map((t) => t.trim());
  if (types.some((t) => file.type === t || t.endsWith('/*'))) return true;
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  return types.some((t) => t.includes(ext) || t === `image/${ext}`);
}

/** 将 value/filenames 解析为列表；value 为逗号分隔 URL，单张时无逗号也可 */
function parseValueToList(value?: string, filenames?: string): { file_url: string; file_name: string }[] {
  const urls = typeof value === 'string' && value.trim()
    ? value.split(',').map((s) => s.trim()).filter(Boolean)
    : [];
  const names = typeof filenames === 'string' && filenames.trim()
    ? filenames.split(',').map((s) => s.trim())
    : [];
  return urls.map((url, i) => ({ file_url: url, file_name: names[i] || '' }));
}

export interface FileListItem {
  file_url: string;
  file_name: string;
}

export interface AvatarUploadProps {
  id?: string;
  /** 已选文件：单张为 URL 字符串，多张为逗号分隔 URL（与文档 files version=1 一致） */
  value?: string;
  /** 已选文件名：逗号分隔，与 value 顺序一致（与文档 filenames 一致） */
  filename?: string;
  onChange?: (url: string, file?: File, response?: unknown) => void;
  /** 多图时回传逗号拼接的 URL；单图时为单 URL */
  onChangeFileName?: (fileNames: string) => void;
  onChangeFileList?: (list: FileListItem[]) => void;
  onDelete?: (item: { file_url: string; file_name?: string }) => void;
  accept?: string;
  acceptedFiles?: string;
  /** 最大图片张数，默认 9 */
  maxFiles?: number;
  maxSizeMB?: number;
  uploadUrl?: string;
  /** 已移除「确认上传/取消」按钮，选图后立即上传；保留 prop 兼容，忽略 true */
  confirmBeforeUpload?: boolean;
  uploadText?: string;
  disabled?: boolean;
  isAllow?: boolean;
  version?: 1 | 2;
  cdnPrefix?: string;
  allowClear?: boolean;
  className?: string;
}

function getDisplayUrl(url: string, cdnPrefix: string): string {
  if (!url) return url;
  if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) return url;
  return `${cdnPrefix.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
}

const AvatarUpload: React.FC<AvatarUploadProps> = ({
  id,
  value,
  filename: filenameProp,
  onChange,
  onChangeFileName,
  onChangeFileList,
  onDelete,
  accept: acceptProp,
  acceptedFiles,
  maxFiles = DEFAULT_MAX_FILES,
  maxSizeMB = DEFAULT_MAX_SIZE_MB,
  uploadUrl,
  uploadText = '上传',
  disabled = false,
  isAllow = true,
  version = 1,
  cdnPrefix: cdnPrefixProp,
  allowClear = true,
  className = '',
}) => {
  const accept = acceptProp ?? acceptedFilesToAccept(acceptedFiles ?? '');
  const cdnPrefix =
    cdnPrefixProp ??
    (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env?.VITE_CDN_API) ??
    DEFAULT_CDN_PREFIX;

  const [fileList, setFileList] = useState<FileListItem[]>(() =>
    parseValueToList(value, filenameProp)
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFileList(parseValueToList(value, filenameProp));
  }, [value, filenameProp]);

  const syncToParent = useCallback(
    (list: FileListItem[]) => {
      const urls = list.map((i) => i.file_url).join(',');
      const names = list.map((i) => i.file_name).join(',');
      onChange?.(urls || '', undefined, undefined);
      onChangeFileName?.(names);
      if (version === 2 && onChangeFileList) onChangeFileList(list);
    },
    [onChange, onChangeFileName, onChangeFileList, version]
  );

  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    if (!isFileAccepted(file, accept)) {
      message.error('仅支持指定类型图片（如 JPG/PNG）');
      return Upload.LIST_IGNORE;
    }
    const isLt = file.size / 1024 / 1024 < maxSizeMB;
    if (!isLt) {
      message.error(`图片大小不能超过 ${maxSizeMB}MB`);
      return Upload.LIST_IGNORE;
    }
    if (fileList.length >= maxFiles) {
      message.error(`最多上传 ${maxFiles} 张图片`);
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const doUpload = useCallback(
    async (file: File) => {
      const res = await uploadFile(file, uploadUrl ? { url: uploadUrl } : undefined);
      const url = getFileUrlFromResponse(res);
      return { res, url: typeof url === 'string' ? url : '' };
    },
    [uploadUrl]
  );

  const customRequest: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    const f = file as File;
    setLoading(true);
    try {
      const { res, url } = await doUpload(f);
      const name = f.name || '';
      const nextList = [...fileList, { file_url: url, file_name: name }];
      setFileList(nextList);
      syncToParent(nextList);
      onSuccess?.(res);
      message.success('上传成功');
    } catch (e) {
      onError?.(e as Error);
      message.error((e as Error)?.message || '上传失败');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = (item: FileListItem) => {
    const nextList = fileList.filter((i) => i.file_url !== item.file_url);
    setFileList(nextList);
    syncToParent(nextList);
    onDelete?.(item);
  };

  const handleChange: UploadProps['onChange'] = (info) => {
    if (info.file.status === 'error') {
      message.error('上传失败');
      setLoading(false);
    }
  };

  const uploadDisabled = disabled || !isAllow;
  const canAdd = fileList.length < maxFiles;
  const hasAny = fileList.length > 0;

  if (!isAllow && !hasAny) {
    return (
      <div id={id} className={`avatar-upload-wrap ${className}`}>
        <div className="avatar-uploader avatar-uploader-readonly">
          <div className="avatar-placeholder">
            <span className="ant-upload-text">暂无文件</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id={id} className={`avatar-upload-wrap avatar-upload-wrap-multi ${className}`}>
      {fileList.map((item) => (
        <div key={item.file_url} className="avatar-upload-preview-wrap">
          <div className="avatar-upload-preview">
            <img src={getDisplayUrl(item.file_url, cdnPrefix)} alt={item.file_name || '图片'} />
            {allowClear && !disabled && (
              <button
                type="button"
                className="avatar-upload-clear"
                onClick={() => handleRemove(item)}
                aria-label="删除"
              >
                <DeleteOutlined />
              </button>
            )}
          </div>
        </div>
      ))}
      {canAdd && (
        <Upload
          name="file"
          listType="picture-card"
          className="avatar-uploader"
          showUploadList={false}
          accept={accept}
          disabled={uploadDisabled}
          beforeUpload={beforeUpload}
          customRequest={customRequest}
          onChange={handleChange}
          multiple={false}
        >
          <div className="avatar-placeholder">
            {loading ? (
              <LoadingOutlined style={{ fontSize: 32, color: '#999' }} />
            ) : (
              <>
                <PlusOutlined style={{ fontSize: 32, color: '#999' }} />
                <div className="ant-upload-text">{uploadText}</div>
              </>
            )}
          </div>
        </Upload>
      )}
    </div>
  );
};

export default AvatarUpload;
